import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from 'app/core/auth/auth.service';
import { environment } from 'environments/environment';
import {
  Observable,
  catchError,
  defer,
  of,
  switchMap,
  tap,
  throwError,
} from 'rxjs';

/** These local API paths are served by Fuse, not Cortex. */
const MOCK_API_PREFIXES = [
  '/api/apps/',
  '/api/common/',
  '/api/dashboards/',
  '/api/pages/',
  '/api/ui/',
];

const isCortexRequest = (url: URL): boolean => {
  if (url.origin === new URL(environment.fusionBaseUrl).origin) return false;
  if (environment.cortexApiUrl) {
    return url.origin === new URL(environment.cortexApiUrl).origin;
  }
  return (
    url.origin === window.location.origin &&
    url.pathname.startsWith('/api/') &&
    !MOCK_API_PREFIXES.some((prefix) => url.pathname.startsWith(prefix))
  );
};

/** Authenticate Cortex only; a request and its single retry belong to one session. */
export const authInterceptor = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
  const auth = inject(AuthService);
  const url = new URL(req.url, document.baseURI);
  if (!isCortexRequest(url)) return next(req);

  return defer(() => {
    const version = auth.sessionVersion;
    const exchange = url.pathname === '/api/auth/login';
    const send = (token: string) => {
      auth.assertSession(version);
      return next(
        req.clone({
          headers: token
            ? req.headers.set('Authorization', 'Bearer ' + token)
            : req.headers.delete('Authorization'),
        }),
      ).pipe(tap(() => auth.assertSession(version)));
    };

    // Product outages may leave a request unauthenticated, but cancellation
    // must not fall through to another request.
    const token$ =
      exchange || !auth.fusionSid
        ? of('')
        : auth
            .ensureCortexToken()
            .pipe(
              catchError((error) =>
                error?.name === 'AbortError' ? throwError(() => error) : of(''),
              ),
            );

    return token$.pipe(
      switchMap(send),
      catchError((error) => {
        auth.assertSession(version);
        if (!(error instanceof HttpErrorResponse) || error.status !== 401) {
          return throwError(() => error);
        }
        if (exchange || !auth.fusionSid) {
          auth.handleUnauthorized();
          return throwError(() => error);
        }
        return auth.refreshAccessToken().pipe(
          switchMap(send),
          catchError((retryError) => {
            auth.assertSession(version);
            if (retryError?.name === 'AbortError') {
              return throwError(() => retryError);
            }
            auth.handleUnauthorized();
            return throwError(() => error);
          }),
        );
      }),
    );
  });
};
